/**
 * HoverTooltip: what's under the brush — the block's population and current
 * district, or how many blocks a larger brush covers — plus the names of
 * any reference layers shown there ("Census Tract 4012", "Oakland city").
 * Fed by the paint tool's onHover callback through a tiny store so hover
 * never re-renders the map view.
 */
import { create } from 'zustand';
import type { HoverInfo } from '../../map/tools/painting';
import type { PlaceName } from '../../map/overlays';
import { formatPopulation } from '../../lib/districtStats';
import { usePaintingStore } from '../../stores/painting';

export type HoverState = HoverInfo & { places?: PlaceName[] };

export const useHoverStore = create<{ info: HoverState | null; set: (info: HoverState | null) => void }>()(
  (set) => ({ info: null, set: (info) => set({ info }) }),
);

export function HoverTooltip(): JSX.Element | null {
  const info = useHoverStore((s) => s.info);
  const populations = usePaintingStore((s) => s.populations);
  const assignments = usePaintingStore((s) => s.assignments);
  const labels = usePaintingStore((s) => s.labels);
  const colors = usePaintingStore((s) => s.colors);
  if (!info || !info.geoid) return null;

  const pop = populations?.[info.geoid];
  const district = assignments[info.geoid];
  const assigned = district !== undefined;

  return (
    <div
      className="pointer-events-none absolute z-30 rounded-lg bg-ink/90 px-2.5 py-1.5 text-[11px] leading-tight text-white shadow-lg"
      style={{ transform: `translate(${info.x + 18}px, ${info.y + 14}px)`, left: 0, top: 0 }}
      aria-hidden="true"
    >
      <p className="font-semibold tabular-nums">
        {pop !== undefined ? `${formatPopulation(pop)} people` : 'Block'}
        {info.count > 1 && <span className="font-normal text-white/70"> · brush covers {info.count} blocks</span>}
      </p>
      <p className="mt-0.5 flex items-center gap-1 text-white/75">
        <span
          className="inline-block h-2 w-2 rounded-sm"
          style={{ backgroundColor: assigned ? colors[district] : 'transparent', boxShadow: assigned ? undefined : 'inset 0 0 0 1px rgba(255,255,255,0.6)' }}
        />
        {assigned ? labels[district] ?? `District ${district + 1}` : 'Unassigned'}
      </p>
      {info.places && info.places.length > 0 && (
        <ul className="mt-1 space-y-0.5 border-t border-white/15 pt-1 text-white/80">
          {info.places.map((place) => (
            <li key={place.layerId} className="flex items-center gap-1">
              <span className="inline-block h-2 w-2 rounded-sm ring-1 ring-white/60" style={{ backgroundColor: place.color }} />
              <span>{place.name}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
